export interface User {
    id: string;
    username: string;
    is_admin?: boolean;
    role?: string;
    pin_hash?: string;
    hourly_pay?: number | null;
}

export interface CompoundingLog {
    id: string;
    formula_id: string;
    formula_name: string;
    batch_number: string;
    batch_number_seq?: number;
    date_compounded: string;
    bud_rule?: string;
    bud_days?: number;
    bud_date?: string;
    dosage_form?: string;
    route_of_administration?: string;
    storage_conditions?: string;
    total_quantity?: number;
    total_quantity_unit?: string;
    technician_id?: string;
    technician_name?: string;
    pharmacist_id?: string;
    pharmacist_name?: string;
    ingredients_used: IngredientUsed[];
    instructions_status: InstructionStatus[];
    instruction_groups_status?: CompoundingStepGroupStatus[];
    equipment: string[];
    qa_results: QAResult[];
    notes?: string;
    status: 'draft' | 'active_compounding' | 'post_fill' | 'lab_processing' | 'completed' | 'verified' | 'rejected' | 'in_progress';
    post_fill_info?: {
        vial_count?: number;
        vial_size?: string;
        vial_size_unit?: string;
        vial_lot?: string;
        stopper_lot?: string;
        cap_lot?: string;
        filter_lot?: string;
        visual_inspection?: string;
        labels_printed?: boolean;
        notes?: string;
        /**
         * Multiple fill volumes captured for a single batch. Many compounded
         * batches are split across two or more vial sizes (e.g. 100 × 2 mL +
         * 25 × 5 mL). When `fills[]` is populated, it is the canonical source
         * of truth and the root `vial_count/vial_size/vial_size_unit` mirror
         * the FIRST row for backward compatibility with older logs / PDFs.
         *
         * Each row also carries its OWN vial/stopper/cap lot numbers because
         * different vial sizes are routinely sourced from different lots —
         * lumping every fill into one shared lot would defeat traceability.
         */
        fills?: Array<{
            id: string;
            vial_count: number;
            vial_size: string;
            vial_size_unit: string;
            vial_lot?: string;
            vial_exp?: string;       // YYYY-MM-DD
            stopper_lot?: string;
            stopper_exp?: string;    // YYYY-MM-DD
            cap_lot?: string;
            cap_exp?: string;        // YYYY-MM-DD
        }>;
        /**
         * Filter Integrity Test (post-filtration bubble-point assay). PASS
         * when actual ≥ expected (the membrane held). FAIL means the filter
         * leaked and the batch may be compromised.
         */
        filter_integrity?: {
            filter_lot?: string;
            expected_bubble_point?: number;
            actual_bubble_point?: number;
            bubble_point_unit?: string;     // psi (default), mbar, kPa
            pass?: boolean;                 // derived: actual >= expected
            notes?: string;
            tested_by?: string;
            tested_at?: string;             // ISO timestamp
        };
        /** Sterile filtration tubing — single value per batch (one set
         *  of tubing is typically used for the entire compounding run). */
        tubing_lot?: string;
        tubing_expiration?: string;         // YYYY-MM-DD
        /**
         * Aggregate visual-inspection discard counts by defect category.
         * Saving this form (even with all zeros) is sufficient to satisfy
         * the sterile-batch visual-inspection gate — the previous behaviour
         * (forcing the tech to log a fake "0 rejects" vial through the modal)
         * was the bug the user reported.
         */
        visual_inspection_counts?: {
            particulates?: number;
            precipitate?: number;
            discoloration?: number;
            container_closure?: number;
            other?: number;
            recorded_by?: string;
            recorded_at?: string;
        };
    };
    lab_processing_info?: {
        lab_name?: string;
        sent_date?: string;
        expected_return_date?: string;
        tracking_number?: string;
        notes?: string;
    };
    signed_log_files?: LogFile[];
    coa_files?: LogFile[];
    lab_receipt_files?: LogFile[];
    is_sterile?: boolean;
    visual_inspection_records?: Array<{
        id: string;
        vial_number: string;
        defect_type: string;
        notes: string;
        marked_non_dispensable: boolean;
        image_data_url: string;
        logged_by: string;
        logged_at: string;
    }>;
    created_at?: string;
    updated_at?: string;
    inventory_decremented?: boolean;
    inventory_decrement_summary?: Array<{
        inventory_item_id: string;
        name: string;
        deducted: number;
        unit: string;
        old_qty: number;
        new_qty: number;
    }>;
    status_changed_at?: string;
    status_history?: Array<{
        status: string;
        at: string;
        by?: string;
        from_status?: string;
    }>;
    linked_orders?: Array<{
        order_id: string;
        rx_number?: string;
        patient_name?: string;
        patient_id?: string;
        linked_at?: string;
        linked_by?: string;
    }>;
}

export interface IngredientWeightRecord {
    actual_weight: number;
    target_weight: number;
    unit: string;
    variance_pct: number;
    tolerance_pct: number;
    scale_offline: boolean;
    verified_by_user: string;
    verified_by_pin_user?: string;
    recorded_at: string;
    verification?: {
        ndc_match?: boolean;
        lot_match?: boolean;
        name_match?: boolean;
        all_pass?: boolean;
    };
}

export interface IngredientLotUpdateHistoryEntry {
    previous: { ndc: string; lot_number: string; manufacturer: string; expiration_date: string };
    new: { ndc: string; lot_number: string; manufacturer: string; expiration_date: string };
    reason: string;
    approved_by: string;
    approved_by_role: string;
    approved_at: string;
    performed_by: string;
}

export interface IngredientUsed {
    id?: string;
    ingredient_name: string;
    target_quantity: number;
    actual_quantity: number;
    unit: string;
    lot_number?: string;
    ndc?: string;
    expiration_date?: string;
    manufacturer?: string;
    inventory_item_id?: string;
    weight_record?: IngredientWeightRecord;
    lot_update_history?: IngredientLotUpdateHistoryEntry[];
    photos?: Array<{
        id: string;
        image_data_url: string;
        capture_kind: 'live' | 'pharmacist_upload';
        note: string;
        captured_by: string;
        captured_at: string;
    }>;
}

export interface InstructionStatus {
    step: string;
    completed: boolean;
    completed_by?: string;
    completed_at?: string;
}

// Hierarchical compounding instructions ──────────────────────────────
// Each parent group is auto-labeled A, B, C, … on the UI; each sub-step
// inside is auto-numbered 1, 2, 3, …
export interface CompoundingSubStep {
    id?: string;
    kind: 'text' | 'checkbox' | 'lot_capture';
    text: string;
    lot_fields?: string[];   // names like "cap lot", "stopper lot"
}

export interface CompoundingStepGroup {
    id?: string;
    text: string;
    sub_steps: CompoundingSubStep[];
}

// Per-batch status mirrors the formula's instruction_groups but adds
// completion + lot_values captured during compounding.
export interface CompoundingSubStepStatus {
    id?: string;
    kind: 'text' | 'checkbox' | 'lot_capture';
    text: string;
    lot_fields?: string[];
    lot_values?: Record<string, string>;
    completed: boolean;
    completed_by?: string;
    completed_at?: string;
}

export interface CompoundingStepGroupStatus {
    id?: string;
    text: string;
    sub_steps: CompoundingSubStepStatus[];
}

export interface QAResult {
    check_name: string;
    expected_value: string;
    actual_value?: string;
    pass: boolean;
    checked_by?: string;
    checked_at?: string;
}

export interface LogFile {
    file_id: string;
    original_name: string;
    stored_name: string;
    file_type: 'signed_log' | 'coa';
    uploaded_by?: string;
    uploaded_at?: string;
}

export interface SizeVariant {
    size: string;
    quantity: number;
    price_per_unit: number;
}

export interface InventoryItem {
    id: string;
    name: string;
    quantity_on_hand: number;
    unit: string;
    lot_number?: string;
    expiration_date?: string;
    ndc?: string;
    barcode?: string;
    manufacturer?: string;
    reorder_point?: number;
    category?: string;
    size_variants?: SizeVariant[];
}

export interface Settings {
    scale_baud_rate?: number;
    scale_data_bits?: number;
    scale_stop_bits?: number;
    scale_parity?: 'none' | 'even' | 'odd';
    scale_precision?: number;
    tolerance_percent?: number;
    enforce_barcode_scan?: boolean;
}

export interface PinRequirements {
    pin_enabled?: boolean;
    create_task?: boolean;
    edit_task?: boolean;
    delete_task?: boolean;
    change_status?: boolean;
    add_entry?: boolean;
    admin_actions?: boolean;
    create_formula?: boolean;
    create_compounding_log?: boolean;
    complete_compounding_log?: boolean;
    verify_compounding_log?: boolean;
    [key: string]: boolean | undefined;
}

/** Thrown by login when backend requires 2FA verification. */
export class TwoFactorRequiredError extends Error {
    requires_2fa = true;
    temp_token: string;
    constructor(tempToken: string) {
        super('2FA_REQUIRED');
        this.name = 'TwoFactorRequiredError';
        this.temp_token = tempToken;
    }
}

export interface Task {
    id: string;
    title: string;
    description?: string;
    status: string;
    priority?: string;
    category_id?: string;
    assigned_to?: string;
    due_date?: string;
    due_time?: string;
    created_at?: string;
    updated_at?: string;
    is_overdue?: boolean;
    recurrence?: RecurrenceConfig;
    checklist?: ChecklistItem[];
    data_fields?: DataFieldItem[];
    comments?: string;
}

export interface RecurrenceConfig {
    type: 'none' | 'daily' | 'weekly' | 'monthly' | 'custom' | 'weekdays';
    interval?: number;
    days_of_week?: number[];
    day_of_month?: number;
    end_date?: string | Date | null;
}

export interface ChecklistItem {
    id: string;
    text: string;
    checked: boolean;
    checked_by_user_id?: string;
    checked_by_username?: string;
    checked_at?: string;
}

export interface DataFieldItem {
    id: string;
    label: string;
    value: string;
    filled_by_user_id?: string;
    filled_by_username?: string;
    filled_at?: string;
}

export interface Category {
    id: string;
    name: string;
    color: string;
}

export interface Template {
    id: string;
    name?: string;
    description: string;
    title: string;
    priority: string;
    category_id?: string;
    recurrence?: RecurrenceConfig;
    checklist?: ChecklistItem[];
    data_fields?: DataFieldItem[];
    due_time?: string;
    comments?: string;
    created_at?: string;
}

export interface UserProfile {
    user_id: string;
    full_name?: string;
    email?: string;
    phone?: string;
    emergency_contact_name?: string;
    emergency_contact_phone?: string;
    address?: string;
    certifications?: string;
    notes?: string;
    [key: string]: string | undefined;
}

export interface FormulaIngredient {
    name: string;
    quantity: number;
    unit: string;
    notes?: string;
    // Legacy / deprecated — present on older records but no longer collected
    // through the Formula builder UI. NDC is captured per inventory batch.
    ndc?: string;
    lot_number?: string;
    expiration_date?: string;
    manufacturer?: string;
    ingredient_name?: string;  // legacy alias seen in some old records
}

export interface FormulaQACheck {
    check_name: string;
    expected_value: string;
}

export interface Formula {
    id?: string;
    name: string;
    description?: string;
    dosage_form?: string;
    route_of_administration?: string;
    storage_conditions?: string;
    bud_rule?: string;
    bud_days?: number;
    total_quantity?: number;
    total_quantity_unit?: string;
    ingredients: FormulaIngredient[];
    instructions: string[];
    instruction_groups?: CompoundingStepGroup[];
    equipment: string[];
    qa_checks: FormulaQACheck[];
    created_at?: string;
    updated_at?: string;
}
